import type ContextService from "@core-ui/ContextServices/ContextService";
import type { ReportedSearch } from "@ext/serach/components/hooks/useSearchAnalytics";
import type { CatalogSearchScope } from "@ext/serach/components/model/searchScope";
import type { ResourceFilter } from "@ext/serach/Searcher";
import {
	createContext,
	type Dispatch,
	type MutableRefObject,
	type ReactElement,
	type SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

export interface SearchQueryServiceValue {
	query: string;
	setQuery: Dispatch<SetStateAction<string>>;
	resourceFilter: ResourceFilter;
	setResourceFilter: Dispatch<SetStateAction<ResourceFilter>>;
	hasOpenRequest: boolean;
	requestedScopeFilter?: CatalogSearchScope;
	requestOpen: (scopeFilter?: CatalogSearchScope) => void;
	clearOpenRequest: () => void;
	/** Lives next to the query so a search restored after remounting the dialog is not reported twice. */
	lastReportedSearch: MutableRefObject<ReportedSearch | null>;
}

const SearchQueryContext = createContext<SearchQueryServiceValue>(undefined);
class SearchQueryService implements ContextService {
	Init({ children }: { children: ReactElement }): ReactElement {
		const [query, setQuery] = useState<string>("");
		const [resourceFilter, setResourceFilter] = useState<ResourceFilter>("without");
		const [hasOpenRequest, setHasOpenRequest] = useState<boolean>(false);
		const [requestedScopeFilter, setRequestedScopeFilter] = useState<CatalogSearchScope | undefined>(undefined);
		const lastReportedSearch = useRef<ReportedSearch | null>(null);

		// A query typed anew is a new search
		useEffect(() => {
			lastReportedSearch.current = null;
		}, [query]);

		const requestOpen = useCallback((scopeFilter?: CatalogSearchScope) => {
			setRequestedScopeFilter(scopeFilter);
			setHasOpenRequest(true);
		}, []);

		const clearOpenRequest = useCallback(() => {
			setHasOpenRequest(false);
			setRequestedScopeFilter(undefined);
		}, []);

		return (
			<SearchQueryContext.Provider
				value={{
					query,
					setQuery,
					resourceFilter,
					setResourceFilter,
					hasOpenRequest,
					requestedScopeFilter,
					requestOpen,
					clearOpenRequest,
					lastReportedSearch,
				}}
			>
				{children}
			</SearchQueryContext.Provider>
		);
	}

	get value(): SearchQueryServiceValue {
		return useContext(SearchQueryContext);
	}
}

export default new SearchQueryService();
